import Bugsnag, { BreadcrumbType } from '@bugsnag/js'
import { Client } from '@bugsnag/core'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import { BugsnagConfig } from '@/utils/app/config'

let bugsnagClient: Client | null = null

/**
 * Get bugsnag client instance.
 *
 * for more information, see: https://docs.bugsnag.com/platforms/javascript/react/
 *
 * @returns {Client} Bugsnag client
 */
export const getBugsnagClient = (config: BugsnagConfig): Client => {
  if (!bugsnagClient || !Bugsnag.isStarted()) {
    bugsnagClient = Bugsnag.start({
      apiKey: config.apiKey,
      appType: typeof window !== 'undefined' ? 'browser' : 'server',
      endpoints: {
        notify: config.notifyUrl,
        sessions: config.sessionsUrl,
      },
      collectUserIp: false,
      generateAnonymousId: true,
      autoDetectErrors: true,
      autoTrackSessions: true,
      trackInlineScripts: true,
      enabledReleaseStages: ['prod', 'preprod', 'preview', 'development'],
      releaseStage: config.releaseStage,
      appVersion: config.appVersion,
      plugins: [new BugsnagPluginReact()],
    })
  }
  return bugsnagClient
}

/**
 * Notify bugsnag with an error.
 * @param message {string} The breadcrumb message
 * @param metadata {[p: string]: any | undefined} All additional data to send with the error
 * @param type {BreadcrumbType | undefined} The type of the breadcrumb
 */
export const leaveBreadcrumb = (
  message: string,
  metadata: { [p: string]: any } | undefined,
  type?: BreadcrumbType,
): void => {
  if (bugsnagClient) {
    bugsnagClient.leaveBreadcrumb(message, metadata, type)
  }
}

export const notifyError = (
  message: string,
  metadata: { [p: string]: any },
): void => {
  if (bugsnagClient) {
    bugsnagClient.notify(new Error(message), (event) => {
      event.addMetadata('meta', metadata)
    })
  }
}

/**
 * Add metadata to bugsnag.
 * @param key {string} The metadata key
 * @param value {[p: string]: any} The metadata value
 */
export const addMetadata = (key: string, value: { [p: string]: any }): void => {
  if (bugsnagClient) {
    bugsnagClient.addMetadata(key, value)
  }
}
